import { useMutation } from '@apollo/client';
import { CREATE_LOOKBOOK } from '@/shared/mutations';
import {
  MutationResult,
  MutationCreateLookbookArgs,
} from '@/shared/types/generated';
import useStore from '@/stores/useStore';
import { useGetUser } from '@/shared/hooks/useGetUser';
import SentryService from '@/services/sentry/Sentry.service';

export function useCreateLookbook() {
  const setSnackbarState = useStore((state) => state.setSnackbarState);
  const { user } = useGetUser();

  const [createLookbookMutation, { loading }] = useMutation<
    MutationResult<'createLookbook'>,
    MutationCreateLookbookArgs
  >(CREATE_LOOKBOOK);

  const createLookbook = async (params: {
    sendbirdMessageId: string;
    price: number; // Price in cents
    stylistId: number; // Stylist ID from the message data
    note?: string; // Optional note from stylist
  }) => {
    try {
      const { sendbirdMessageId, price, stylistId, note } = params;

      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data } = await createLookbookMutation({
        variables: {
          input: {
            sendbirdMessageId,
            stylistId,
            price,
            ...(note && { note }),
          },
        },
      });

      if (!data?.createLookbook) {
        throw new Error('Failed to create lookbook');
      }

      return data.createLookbook;
    } catch (error) {
      SentryService.captureError('Error creating lookbook', error);
      setSnackbarState({
        open: true,
        message: 'Failed to open purchasing view. Please try again.',
        severity: 'error',
      });
      throw error;
    }
  };

  return {
    createLookbook,
    loading,
  };
}
